import moment from 'moment';
import { toNumber } from 'lodash';

import * as allNumbersHelpers from '@/js/store/calculator/utilities/allNumbersHelpers';

export {
  initializeValues,
  getAllNumbersByMonthForCareerProgression,
  generateServiceMemberTsp,
  getAllNumbersByMonthFromRunDateToDeathDateTodaysDollars,
  getAllNumbersByMonthFromRunDateToDeathDateFutureDollars,
  getPayTableMonthlyPay,
} from '@/js/store/calculator/utilities/allNumbersHelpers';

//  ****************************************************************************************************
//    Add the current monthly pay amount (Today's $) to each record in the array based on years of
//    service completed and rank.
//
//    For records prior to the run date year/month, the GlobalSettings-->PayTables are utilized. If
//    the year/month are older than the oldest pay table, the oldest pay table is utilized.
//
//    The current monthly pay amount (Today's $) is based on the years of completed service and rank.
//  ****************************************************************************************************
function addPayAmountTodaysDollars(careerMonths, serviceMember, rundate, pebd, retSepDate) {
  const { payTables } = serviceMember;
  const pebdPlus3Months = moment(pebd).add(2, 'months');
  const pebdPlus4Months = moment(pebd).add(3, 'months');
  const pebdPlus6Months = moment(pebd).add(5, 'months');
  let rank = '';
  let pebdserviceYear = 0;
  let payGrade = '';

  //  If Hi3 past years are being utilized, start from the prior date needed to fulfill 36 months of data.
  let startDate = rundate;
  if (serviceMember.isEligibleForRetirementPension() && serviceMember.numberOfMonthsRemainingInServiceCareer() < 36) {
    startDate = moment(rundate).subtract(36 - serviceMember.numberOfMonthsRemainingInServiceCareer(), 'months');
  }

  careerMonths.forEach((careerMonth) => {
    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  When a non-regular retirement pension is being implemented, the "Pay Todays $" must be provided thru the month before the first tsp payout.
    //  When a regular retirement pension is begin implemented, only need record up to and including the retSep date. But don't know what the
    //  retirement type is at this point, so just extend the pay amounts out to 1 month prior to TSP being paid out.
    const endDate = moment(serviceMember.tspWithdrawalDate()).subtract(1, 'months');

    careerMonth.payAmountTodaysDollars = 0;
    if (calendarDate.isSameOrAfter(startDate) && calendarDate.isSameOrBefore(endDate)) {
      //  Determine rank basked on the calendar date.
      //    1.  If the calendar date is prior to the run date, obtain the rank associated to the month. Rank for each month for lookback years was manipulated in 'addOverrideCareerProgression'.
      //    2.  If the calendar date is within the 1st three months of career, make E-1<4 (in boot camp, lower pay than regular E-1 rank).
      //    3.  if the calendar date is within month 4 thru 6, make E-1 (pay increase from completing boot camp).
      //    2.  After retSepDate, rank will retain to the last rank obtained while in service (required to determine TSP payment for non-regular retirement).
      if (calendarDate.isSameOrAfter(startDate) && calendarDate.isBefore(rundate)) {
        rank = careerMonth.overrideRank;
      } else if (serviceMember.currentRank === 'E-1' && calendarDate.isSameOrAfter(pebd) && calendarDate.isSameOrBefore(pebdPlus3Months)) {
        rank = 'E-1<4';
      } else if (serviceMember.currentRank === 'E-1' && calendarDate.isSameOrAfter(pebdPlus4Months) && calendarDate.isSameOrBefore(pebdPlus6Months)) {
        rank = 'E-1';
      } else if (calendarDate.isSameOrAfter(rundate) && calendarDate.isSameOrBefore(retSepDate)) {
        rank = careerMonth.overrideRank;
      }

      pebdserviceYear = calendarDate.isSameOrBefore(retSepDate) ? careerMonth.pebdServiceYear : pebdserviceYear;

      //  Non-regular retirement requires the 'Pay Today/Future $' to be projected up to 1 month prior to the non-regular retirement withdrawal age.
      pebdserviceYear = (calendarDate.isAfter(retSepDate) && calendarMonth === pebd.month() + 1) ? pebdserviceYear + 1 : pebdserviceYear;

      payGrade = calendarDate.isSameOrBefore(retSepDate) ? serviceMember.currentPayGrade(rank) : payGrade;

      let yearsDifferenceCalendarDateFromRundate = calendarYear > rundate.year() ? 0 : rundate.year() - calendarYear;
      yearsDifferenceCalendarDateFromRundate = yearsDifferenceCalendarDateFromRundate > 3 ? 3 : yearsDifferenceCalendarDateFromRundate; //  3 years in past is max.
      const payTableFilename = `runDateMinus${yearsDifferenceCalendarDateFromRundate}${(yearsDifferenceCalendarDateFromRundate === 1 ? 'Year' : 'Years')}`;

      careerMonth.payAmountTodaysDollars = allNumbersHelpers.getPayTableMonthlyPay(payTables, payTableFilename, payGrade, rank, pebdserviceYear);
    }
  });

  return careerMonths;
}

//  ****************************************************************************************************
//    Add the current monthly pay amount (Future $) to each record in the array.
//
//    For records equal to the run date year, the pay amount is equal to the today's dollar pay amount.
//
//    For records greater than the run date year, the associated GlobalSettings-->PayTableGrowthRate
//    multiplier is used.
//    Example:
//      1.  Run date year is 2020
//      2.  Future dollay current pay amount = today's current pay amount.
//      3.  In 2021, the current pay amount (Today's $) is multiplied/added by
//          GlobalSettings-->PayTableGrowthRates-->Year1.
//      4.  In 2022, the current pay amount (Today's $) is multiplied/added by
//          GlobalSettings-->PayTableGrowthRates-->Year1 as well ass
//          GlobalSettings-->PayTableGrowthRates-- > Year2.
//  ****************************************************************************************************
function addPayAmountFutureDollars(careerMonths, serviceMember, rundate) {
  const rundateMinus1Month = moment(rundate).subtract(1, 'months');

  const payTableGrowthRateYear1 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year1) / 100 + 1;
  const payTableGrowthRateYear2 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year2) / 100 + 1;
  const payTableGrowthRateYear3 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year3) / 100 + 1;
  const payTableGrowthRateYear4 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year4) / 100 + 1;
  const payTableGrowthRateYear5 = toNumber(serviceMember.globalSettings.payTableGrowthRates.year5) / 100 + 1;

  //  If Hi3 past years are required, start from the prior date needed to fulfill 36 months of data.
  const hi3PastYearsRequired = serviceMember.isEligibleForRetirementPension && serviceMember.numberOfMonthsRemainingInServiceCareer() < 36;
  let hi3PastYearsStartDate = rundate;
  if (hi3PastYearsRequired) {
    hi3PastYearsStartDate = moment(rundate).subtract(36 - serviceMember.numberOfMonthsRemainingInServiceCareer(), 'months');
  }

  for (let index = 0; index <= careerMonths.length - 1; index += 1) {
    const careerMonth = careerMonths[index];

    const calendarYear = Number(careerMonth.calendarYear);
    const calendarMonth = Number(careerMonth.calendarMonth);
    const calendarDate = moment([calendarYear, calendarMonth - 1, 1]);

    //  When a non-regular retirement pension is being implemented, the "Pay Todays $" must be provided thru the month before the first tsp payout.
    //  When a regular retirement pension is begin implemented, only need record up to and including the retSep date. But don't know what the
    //  retirement type is at this point, so just extend the pay amounts out to 1 month prior to TSP being paid out.
    const endDate = moment(serviceMember.tspWithdrawalDate()).subtract(1, 'months');

    careerMonth.payAmountFutureDollars = 0;

    if (calendarDate.isSameOrAfter(hi3PastYearsStartDate) && calendarDate.isSameOrBefore(endDate)) {
      let payAmountFutureDollars = 0;
      const payAmountTodaysDollars = toNumber(careerMonth.payAmountTodaysDollars);

      if (hi3PastYearsRequired && calendarDate.isSameOrBefore(rundateMinus1Month)) {
        //  Past years monies do not include adjustment for inflation.
        careerMonth.payAmountFutureDollars = payAmountTodaysDollars;
        continue;
      } else if (calendarYear === rundate.year()) {
        //  No adjustment for 1st calendar year.
        payAmountFutureDollars = payAmountTodaysDollars;
      } else if (calendarYear >= (rundate.year() + 1)) {
        //  Adjust for inflation.
        const calendarYearFromRundateYearOffset = calendarYear - rundate.year();

        payAmountFutureDollars = payAmountTodaysDollars * payTableGrowthRateYear1;
        if (calendarYearFromRundateYearOffset >= 2) payAmountFutureDollars *= payTableGrowthRateYear2;
        if (calendarYearFromRundateYearOffset >= 3) payAmountFutureDollars *= payTableGrowthRateYear3;
        if (calendarYearFromRundateYearOffset >= 4) payAmountFutureDollars *= payTableGrowthRateYear4;
        if (calendarYearFromRundateYearOffset >= 5) payAmountFutureDollars *= payTableGrowthRateYear5;
        if (calendarYearFromRundateYearOffset >= 6) {
          for (let yearOffset = 6; yearOffset <= calendarYearFromRundateYearOffset; yearOffset += 1) {
            payAmountFutureDollars *= payTableGrowthRateYear5;
          }
        }
      }

      careerMonth.payAmountFutureDollars = payAmountFutureDollars;
    }
  }

  return careerMonths;
}

export function getAllNumbersByMonth(serviceMember) {
  // these functions are being overwritten
  return allNumbersHelpers.getAllNumbersByMonth(serviceMember, {
    addPayAmountTodaysDollars,
    addPayAmountFutureDollars,
  });
}

export function generateHi3Pension(serviceMember) {
  let { allNumbersByMonth } = serviceMember.results;
  const retSepDate = serviceMember.retSepDate();
  let paymentStartDate = null;

  if (serviceMember.pensionRetirementType() === serviceMember.RETIREMENT_ELIGIBILITIES.REGULAR_RETIREMENT) {
    paymentStartDate = serviceMember.firstMonthOutOfServiceDate();
    allNumbersByMonth = allNumbersHelpers.addHi3PensionPaymentsRegularRetirementTodaysDollars(allNumbersByMonth, serviceMember, paymentStartDate, retSepDate);
    allNumbersByMonth = allNumbersHelpers.addHi3PensionPaymentsRegularRetirementFutureDollars(allNumbersByMonth, serviceMember, paymentStartDate, retSepDate);
  } else {
    paymentStartDate = serviceMember.nonRegularRetirementWithdrawalDate();
    allNumbersByMonth = allNumbersHelpers.addHi3PensionPaymentsNonRegularRetirementTodaysDollars(allNumbersByMonth, serviceMember, paymentStartDate);
    allNumbersByMonth = allNumbersHelpers.addHi3PensionPaymentsNonRegularRetirementFutureDollars(allNumbersByMonth, serviceMember, paymentStartDate);
  }

  return allNumbersByMonth;
}
